import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Info, X } from 'lucide-react';

import GenericSelect from '../../components/ui/GenericSelect';
import FunnelPlot from './components/FunnelPlot';
import SurvivalCurve from './components/SurvivalCurve';
import { useHrAnalyticsApi, resolveDefaultScope } from './config';

// Devir ve Kohort — BİRLEŞİK sayfa (2026-08-03, spec:
// docs/superpowers/specs/2026-08-03-ha-devir-kohort-birlesik-sayfa-design.md).
//
// Eski Karşılaştırma sayfasının (funnel plot) TÜM içeriği buraya taşındı: iki grafik aynı
// sorunun iki yarısını cevaplıyor ("nerede kaybediyoruz" — funnel, "ne zaman kaybediyoruz" —
// hayatta kalma eğrisi) ve tek sayfada üst üste okunmaları ürün kararıdır (kullanıcı onayı,
// menü etiketi DEĞİŞMEDİ). /hr-analytics/comparison rotası buraya yönlenir (routes/index.js).
//
// Funnel bölümünün davranışı Karşılaştırma sayfasından DEĞİŞMEDEN taşındı:
//   - turnover.byScope.funnel katalogdaki TEK funnel-şekilli metrik; kullanıcıya bırakılan tek
//     seçim KIRILIM (kapsam türü).
//   - Varsayılan kapsam HaSettings.PrimaryScopeOrder'dan (resolveDefaultScope).
//   - GetFunnelAsync period'suz çağrılır, yalnız EN SON dönemin hücreleri gösterilir.
//
// Hayatta kalma bölümünde KAPSAM SEÇİCİ BİLİNÇLİ OLARAK YOK: survival'da her kapsam GRUBU ayrı
// bir eğridir, "tür" seçmek hangi birimin çizileceğini belirlemez — yalnız Şirket Geneli
// (tenant) eğrisi gösterilir (Task 15'in aynı gerekçesi, SmartStatsRow'un scope:'tenant'
// emsali).
const METRIC_CODE = 'turnover.byScope.funnel';
const GRAIN = 'month';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  box-shadow: 0 1px 4px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.06);
  padding: 20px 24px;
`;

const Title = styled.h2`
  margin: 0 0 4px 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Bilgi ikonu — "nasıl okunur" içeriği 2026-08-03 kullanıcı kararıyla sayfadan modala taşındı
// (kutu kalıcı yer kaplıyordu; bilgiye ihtiyaç duyan tıklayıp okur).
const InfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary.main};
  cursor: pointer;

  &:hover { background: ${({ theme }) => `${theme.colors.primary.main}11`}; }
`;

const Description = styled.p`
  margin: 0 0 16px 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  max-width: 760px;
`;

const Controls = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 20px;
  max-width: 280px;
`;

const StateMessage = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

// "Nasıl okunur" modalı — Overlay/DialogBox deseni AiChatModal'la aynı aile, renkler tema
// token'ından (hardcode hex YOK, frontend/CLAUDE.md).
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.4);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding-top: 60px;
  overflow-y: auto;
`;

const DialogBox = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  padding: 24px 28px;
  max-width: 640px;
  width: 90%;
  box-shadow: 0 16px 48px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.15);
  margin-bottom: 40px;
`;

const DialogHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;

  &:hover { background: ${({ theme }) => `${theme.colors.primary.main}11`}; }
`;

const HowToTitle = styled.h3`
  margin: 0 0 8px 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HowToList = styled.ul`
  margin: 0 0 8px 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  li {
    font-family: ${({ theme }) => theme.metrics.font.family};
    font-size: ${({ theme }) => theme.metrics.font.size.sm};
    color: ${({ theme }) => theme.colors.text.primary};
    line-height: 1.45;
  }
`;

const HowToMethod = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

export default function HrAnalyticsTurnoverPage() {
  const { t } = useTranslation();
  const { getCatalog, getSettings, getFunnel, getSurvival } = useHrAnalyticsApi();

  // ── Funnel (Karşılaştırma) durumu ────────────────────────────────────────
  const [supportedScopes, setSupportedScopes] = useState([]);
  const [scope, setScope] = useState(null);
  const [funnelReady, setFunnelReady] = useState(false);
  const [funnelResult, setFunnelResult] = useState(null);
  const [funnelFetching, setFunnelFetching] = useState(true);

  // ── Hayatta kalma durumu ────────────────────────────────────────────────
  const [survivalResult, setSurvivalResult] = useState(null);
  const [survivalFetching, setSurvivalFetching] = useState(true);

  // "Nasıl okunur" modalı (2026-08-03: kutu sayfadan modala taşındı).
  const [howToOpen, setHowToOpen] = useState(false);

  // Adım 1 — katalog + ayarlar: metriğin desteklediği kapsamlar (SupportedScopes, SABİT
  // KODLANMAZ — turnover.byScope.funnel'de Tenant yok, bkz. HaMetricCatalog.cs) ve varsayılan
  // kapsam seçimi (HaSettings.PrimaryScopeOrder, bkz. resolveDefaultScope). Hata toast'ı YOK
  // (CLAUDE.md) — hatalar yalnız Axios interceptor'da ele alınır.
  useEffect(() => {
    let cancelled = false;
    Promise.all([getCatalog(), getSettings()]).then(([catalogRes, settingsRes]) => {
      if (cancelled) return;
      const catalog = catalogRes?.data?.data ?? [];
      const metric = catalog.find((m) => m.code === METRIC_CODE);
      const scopes = metric?.supportedScopes ?? [];
      const order = settingsRes?.data?.data?.primaryScopeOrder ?? [];
      setSupportedScopes(scopes);
      setScope(resolveDefaultScope(order, scopes));
      setFunnelReady(true);
    });
    return () => { cancelled = true; };
  }, [getCatalog, getSettings]);

  // Adım 2 — kapsam belirlendiğinde/değiştiğinde funnel verisini çek.
  useEffect(() => {
    if (!scope) return undefined;
    let cancelled = false;
    setFunnelFetching(true);
    getFunnel({ code: METRIC_CODE, scope, grain: GRAIN })
      .then((res) => {
        if (cancelled) return;
        setFunnelResult(res?.data?.data ?? null);
      })
      .finally(() => {
        if (!cancelled) setFunnelFetching(false);
      });
    return () => { cancelled = true; };
  }, [scope, getFunnel]);

  // Hayatta kalma eğrisi — scope açıkça 'tenant' (bkz. dosya başı yorumu).
  useEffect(() => {
    let cancelled = false;
    setSurvivalFetching(true);
    getSurvival({ scope: 'tenant' })
      .then((res) => {
        if (!cancelled) setSurvivalResult(res?.data?.data ?? null);
      })
      .finally(() => {
        if (!cancelled) setSurvivalFetching(false);
      });
    return () => { cancelled = true; };
  }, [getSurvival]);

  // HaMetricsDataAccess.GetFunnelAsync period parametresi verilmezse TÜM dönemlerin
  // hücrelerini döner (bkz. o sınıfın yorumu) — bu bölüm "şu anki durum" görünümü olduğundan
  // yalnız EN SON dönemin (PeriodStart'ı en büyük) hücrelerini gösterir.
  const latestPeriodCells = useMemo(() => {
    const cells = funnelResult?.cells;
    if (!Array.isArray(cells) || cells.length === 0) return [];
    const latestTime = Math.max(...cells.map((c) => new Date(c.periodStart).getTime()));
    return cells.filter((c) => new Date(c.periodStart).getTime() === latestTime);
  }, [funnelResult]);

  const scopeOptions = useMemo(
    () => supportedScopes.map((s) => ({ id: s, name: t(`hrAnalytics.scopes.${s}`) })),
    [supportedScopes, t]
  );

  const funnelLoading = !funnelReady || funnelFetching;
  const funnelLocked = !funnelLoading && funnelResult && funnelResult.state !== 'available';

  const survivalLocked = !survivalFetching && survivalResult && survivalResult.state !== 'available';

  return (
    <Container>
      <Card>
        <TitleRow>
          <Title>{t('hrAnalytics.comparison')}</Title>
          <InfoButton
            type="button"
            data-role="funnel-how-to-open"
            aria-label={t('hrAnalytics.funnelPlot.howToRead.title')}
            title={t('hrAnalytics.funnelPlot.howToRead.title')}
            onClick={() => setHowToOpen(true)}
          >
            <Info size={18} aria-hidden="true" />
          </InfoButton>
        </TitleRow>
        <Description>{t('hrAnalytics.funnelPlot.description')}</Description>

        <Controls>
          <GenericSelect
            label={t('hrAnalytics.funnelPlot.scopeSelectorLabel')}
            options={scopeOptions}
            value={scope}
            onChange={setScope}
          />
        </Controls>

        {funnelLoading ? (
          <StateMessage>{t('common.loading')}</StateMessage>
        ) : funnelLocked ? (
          <StateMessage>{t(`responseErrors.${funnelResult.reasonKey}`)}</StateMessage>
        ) : (
          <FunnelPlot cells={latestPeriodCells} />
        )}
      </Card>

      <Card>
        <Title>{t('hrAnalytics.turnover')}</Title>
        <Description>{t('hrAnalytics.survivalCurve.description')}</Description>

        {survivalFetching ? (
          <StateMessage>{t('common.loading')}</StateMessage>
        ) : survivalLocked ? (
          <StateMessage>{t(`responseErrors.${survivalResult.reasonKey}`)}</StateMessage>
        ) : (
          <SurvivalCurve points={survivalResult?.points ?? []} />
        )}
      </Card>

      {howToOpen && (
        <Overlay data-role="funnel-how-to-overlay" onClick={() => setHowToOpen(false)}>
          <DialogBox data-role="funnel-how-to-read" onClick={(e) => e.stopPropagation()}>
            <DialogHead>
              <HowToTitle>{t('hrAnalytics.funnelPlot.howToRead.title')}</HowToTitle>
              <CloseButton
                type="button"
                aria-label={t('common.close')}
                onClick={() => setHowToOpen(false)}
              >
                <X size={18} aria-hidden="true" />
              </CloseButton>
            </DialogHead>
            <HowToList>
              {['item1', 'item2', 'item3', 'item4', 'item5'].map((k) => (
                <li key={k}>{t(`hrAnalytics.funnelPlot.howToRead.${k}`)}</li>
              ))}
            </HowToList>
            <HowToMethod>{t('hrAnalytics.funnelPlot.howToRead.method')}</HowToMethod>
          </DialogBox>
        </Overlay>
      )}
    </Container>
  );
}
